import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Headline } from '@oneaudi/unified-web-alpha-components';
import { ButtonList, Container, LabelInput } from './RadioButton.styles';
import { selectInputs, setUserInputs } from '../../../redux/inputsReducer';
import Option from './Option';
import { CheckBoxInputProps } from '../../../types';
import { Form } from '../../../types/analytics';
import { digitalLeadsFormInput } from '../../../utils/analyticsEventListeners';
import { FEATURE_APP_DETAILS } from '../../../constants';
import { convertToPromotionsType } from '../../../functions/convertToPromotionsType';
import { getVehicleForPromotions } from '../../../functions/getVehicleForPromotions';
import { selectDealerAndVehicleData } from '../../../redux/dealerAndVehicleReducer';
import { selectForm, setForm } from '../../../redux/formReducer';
import { selectAppConfig } from '../../../redux/appConfigReducer';

interface RadioButtonProps {
  title: string;
  inputKey: string;
  options: CheckBoxInputProps;
}
const RadioButtonInput: React.FC<RadioButtonProps> = ({ title, inputKey, options }) => {
  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const dispatch = useDispatch();
  const { preferredContactMethod } = useSelector(selectInputs);

  const { offers } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);

  const onHandleChange = (value: string) => {
    const inputTouched = {
      inputTitle: inputKey,
      inputValue: value,
      action: 'select',
      inputType: 'radio',
    };

    // Use vehicle for VTP, vehicleByCarline/vehicleByAudiCode for NGC/MLP, or offers data for promotions
    const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
    const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

    const updatedForm: Form = {
      ...contactForm,
      ...{ formData: { ...contactForm.formData, ...{ preferredContactMethod: value } } },
    };

    dispatch(setForm(updatedForm));
    dispatch(setUserInputs({ inputName: inputKey, inputValue: value }));
    digitalLeadsFormInput(updatedForm, vehicleData, FEATURE_APP_DETAILS, promotion, inputTouched);
  };

  const listOfOptions = Object.keys(options);
  return (
    <Container data-testid="radio-button-wrapper">
      {!process.env.IS_LEGACY ? (
        <Headline
          margin={['0', '0', '0', '0']}
          variant={{ type: 'headline', order: '4', weight: 'normal' }}
          tag="h4"
        >
          {title}
        </Headline>
      ) : (
        <LabelInput>{title}</LabelInput>
      )}

      <ButtonList data-testid={`radio-button-${inputKey}`}>
        {listOfOptions.map((option) => {
          const { value, key } = options[option];
          const checked = preferredContactMethod === value;
          return (
            <Option
              {...options[option]}
              name={inputKey}
              onChange={onHandleChange}
              checked={checked}
              key={key}
            />
          );
        })}
      </ButtonList>
    </Container>
  );
};

export default RadioButtonInput;
